/* eslint-disable @typescript-eslint/no-explicit-any */

import { Box, Button, Flex, Stack, Text, Textarea } from '@chakra-ui/react';
import { fetchAPI } from '@/api/fetch';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AnalyzeOverviewResponse, apiAdminAnalyzeOverview } from '@/api/services';

type FormValues = {
  resumeOverview: string;
  interviewOverview: string;
};

export default function OverviewAnalysisSection() {
  const [submitError, setSubmitError] = useState(false);
  const [overview, setOverview] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors, isSubmitting, isValid }
  } = useForm<FormValues>();

  const handleFormSubmit = async (formData: FormValues) => {
    setSubmitError(false);

    try {
      const response = await fetchAPI<AnalyzeOverviewResponse>({
        request: () =>
          apiAdminAnalyzeOverview({
            payload: {
              resume_overview: formData.resumeOverview,
              interview_overview: formData.interviewOverview
            }
          })
      });

      if (response.success) {
        setOverview(response.data.overview);
      } else {
        handleError('Failed to analyze job requirements', response.message);
      }
    } catch (error) {
      handleError('Failed to analyze job requirements', error);
    }
  };

  const handleError = (message: string, error: any) => {
    console.error(message, error);
    setSubmitError(true);
  };

  const clear = () => {
    reset();
  };

  return (
    <Stack gap="24px">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Flex h="100%" gap="24px" alignItems="flex-start">
          <Stack gap="24px" w="100%">
            <Textarea
              p="16px 24px"
              placeholder="Enter resume overview here..."
              border="1px solid"
              borderColor={errors.resumeOverview ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              resize="vertical"
              minH="100px"
              maxH="100px"
              borderRadius="12px"
              {...register('resumeOverview', {
                required: 'Resume overview is required.'
              })}
            />
            <Textarea
              p="16px 24px"
              placeholder="Enter interview overview here..."
              border="1px solid"
              borderColor={errors.interviewOverview ? 'error.500' : 'neutrals.200'}
              bg="background.secondary"
              fontSize="16px"
              resize="vertical"
              minH="100px"
              maxH="100px"
              borderRadius="12px"
              {...register('interviewOverview', {
                required: 'Interview overview is required.'
              })}
            />
          </Stack>

          <Stack gap="20px">
            <Button
              type="submit"
              loading={isSubmitting}
              disabled={!isValid}
              bg="primary.900"
              fontSize="16px"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
            >
              Analyze
            </Button>
            <Button
              type="button"
              bg="background.secondary"
              color="primary.900"
              fontSize="16px"
              border="1px solid"
              borderColor="primary.900"
              borderRadius="8px"
              p="24px 32px"
              w="150px"
              onClick={clear}
            >
              Cancel
            </Button>
          </Stack>
        </Flex>
        {errors.resumeOverview && (
          <Text fontSize="14px" color="error.700" mt="5px">
            {errors.resumeOverview.message}
          </Text>
        )}
        {errors.interviewOverview && (
          <Text fontSize="14px" color="error.700" mt="5px">
            {errors.interviewOverview.message}
          </Text>
        )}
        {submitError && (
          <Text fontSize="16px" color="error.700" mt="5px">
            Failed to analyze job requirements. Please try again.
          </Text>
        )}
      </form>
      <Text fontSize="18px" color="neutrals.900" fontWeight="semibold">
        Analyzed Overview
      </Text>
      {overview ? (
        <Box w="100%">
          <Text fontSize="16px" color="neutrals.700">
            {overview}
          </Text>
        </Box>
      ) : (
        <Text fontSize="16px" color="neutrals.700">
          No overview analyzed yet.
        </Text>
      )}
    </Stack>
  );
}
